import React, { useContext } from 'react';
import { Accordion } from '@jutro/legacy/components';
import { TranslatorContext } from '@jutro/legacy/locale';
import { WMICCoverageUtil } from 'wmic-pe-capability-quoteandbind-common-react';
import {
    QuoteClauseTable,
    WMICAccordionCard,
    WMICHeading,
    ClausesComponentVM,
} from 'wmic-pe-components-platform-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { CONSTANTS, Position } from 'wmic-pe-portals-utils-js';
import { useAccordionValidation } from 'wmic-pe-portals-wizard-components-ui';
import WMICCPCoverageUtil from 'wmic-pe-capability-gateway-quoteandbind-cp-react/util/WMICCPCoverageUtil';

import messages from './WMICCPBuildingCoverages.messages';
import metadata from './WMICCPBuildingCoverages.metadata.json5';

const { COVERAGE_CATEGORIES, COVERAGE_NAMES } = CONSTANTS;

function WMICCPBuildingCoverages(props) {
    const {
        buildingIndex,
        buildingVM,
        buildingCoverages: buildingCoveragesVM,
        changeSubmissionAndSync,
        changeSubmission,
        syncCoverages,
        recalculate,
        resetQuote,
        onStaleQuoteBranchCode,
        underwritingIssues,
        quoteID,
        onValidate,
        jobVM,
        isBoundPolicy,
        readOnly,
    } = props;

    const translator = useContext(TranslatorContext);
    const { isAccordionValid } = useAccordionValidation(onValidate);

    const buildingCoverages = buildingCoveragesVM?.coverages?.value || [];

    const generateClauseData = coverageColumnData =>
        coverageColumnData.map(({ lob, code }) => ({
            code,
            path: `${lob.path}.coverages.buildingCoverages.children[${buildingIndex}].coverages`,
            clauses: buildingCoverages,
        }));

    const getBuildingCoverages = () => {
        const categoryMap = {
            buildingCoverages: {
                coverageCategory: COVERAGE_CATEGORIES.CP_BUILDING,
                excludedCoverageIDs: [COVERAGE_NAMES.CP_BUSINESS_CONTENTS],
            },
            businessContentsCoverages: {
                coverageIDs: [COVERAGE_NAMES.CP_BUSINESS_CONTENTS],
            },
            buildingAdditionalCoverages: {
                coverageCategory: COVERAGE_CATEGORIES.CP_BUILDING_ADDITIONAL,
            },
        };

        const categorizedCoverages = WMICCoverageUtil.categorizeCoverages(buildingCoverages, categoryMap);

        return Object.keys(categorizedCoverages).map(categoryID => {
            const coverageCategory = categorizedCoverages[categoryID];

            if (!isBoundPolicy) {
                const coverageColumnData = WMICCPCoverageUtil.generateColumnData(jobVM);
                const coverageTableData = [
                    {
                        data: coverageCategory.coverages,
                        tableContent: generateClauseData(coverageColumnData),
                    },
                ];

                return (
                    <>
                        <WMICHeading
                            variant="h2"
                            underlined
                            bold
                            size="h2"
                            title={coverageCategory.categoryName}
                            isValid={isAccordionValid(`clauseTable-${categoryID}`)}
                        />
                        <QuoteClauseTable
                            id={`clauseTable-${categoryID}`}
                            columnData={coverageColumnData}
                            tableData={coverageTableData}
                            quoteID={quoteID}
                            readOnly={readOnly}
                            onChangeSubmissionAndSync={changeSubmissionAndSync}
                            onChangeSubmission={changeSubmission}
                            onSyncCoverages={syncCoverages}
                            onRecalculate={recalculate}
                            onResetQuote={resetQuote}
                            onStaleQuoteBranchCode={onStaleQuoteBranchCode}
                            underwritingIssues={underwritingIssues}
                            filterUWIssuesInCustomOffering={false}
                            showTooltip={false}
                            onValidate={onValidate}
                        />
                    </>
                );
            }

            return (
                <>
                    <WMICHeading variant="h2" underlined bold size="h2" title={coverageCategory.categoryName} />
                    <ClausesComponentVM
                        showAmount={false}
                        isEditable
                        readOnly
                        path={`${categoryID}.coverages`}
                        value={coverageCategory.coverages}
                    />
                </>
            );
        });
    };

    const overrideProps = {
        '@field': {
            labelPosition: Position.LEFT,
            parentNode: buildingVM,
        },
        buildingCoveragesAccordions: {
            content: getBuildingCoverages(),
        },
    };

    const resolvers = {
        resolveComponentMap: {
            Accordion,
        },
    };

    return (
        <WMICAccordionCard
            id={`buildingAccordionCard${buildingIndex}`}
            title={`${translator(messages.buildingNumberHeader, { buildingNumber: buildingVM.buildingNumber.value })}`}
            chevron
            chevronAlignment="left"
            headerVariant="h2"
            headerSize="h2"
            className="gw-mb-0"
            cardBodyClassName="gw-mx-2"
        >
            <ViewModelForm
                model={buildingVM}
                uiProps={metadata.componentContent}
                overrideProps={overrideProps}
                componentMap={resolvers.resolveComponentMap}
            />
        </WMICAccordionCard>
    );
}

export default WMICCPBuildingCoverages;
